import { Download, Plus } from "lucide-react";
import { useState } from "react";

import { cn } from "@/marketing/lib/utils";
import type { PipelineToneStyle } from "@/marketing/lib/pipelineTone";
import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import type {
  CampaignPipelineDefinition,
  PipelineStageDefinition,
} from "@/marketing/models/campaignPipeline";
import { downloadColumnCsv } from "./downloadColumnCsv";

import { CreatorCard } from "./CreatorCard";

interface PipelineColumnProps {
  campaignId: string;
  pipelineDefinition: CampaignPipelineDefinition;
  stage: PipelineStageDefinition;
  toneStyle: PipelineToneStyle;
  creators: CampaignInfluencer[];
  draggingId: string | null;
  // False while a drag is in flight and the dragged creator may not move into this column.
  canDrop: boolean;
  canAdd: boolean;
  onDragStart: (creator: CampaignInfluencer) => void;
  onDragEnd: () => void;
  onDrop: (stage: string, influencerId: string) => void;
  onCardClick: (creator: CampaignInfluencer) => void;
  onAdd: (stage: string) => void;
  tagNameById: Map<string, string>;
  tagCatalogReady: boolean;
  testId?: string;
}

export function PipelineColumn({
  campaignId,
  pipelineDefinition,
  stage,
  toneStyle,
  creators,
  draggingId,
  canDrop,
  canAdd,
  onDragStart,
  onDragEnd,
  onDrop,
  onCardClick,
  onAdd,
  tagNameById,
  tagCatalogReady,
  testId = `pipeline-column-${stage.stage}`,
}: PipelineColumnProps) {
  const [isOver, setIsOver] = useState(false);
  const label = stage.labels.admin;

  return (
    <div
      role="region"
      aria-label={`${label} creators`}
      data-testid={testId}
      aria-disabled={draggingId !== null && !canDrop ? true : undefined}
      onDragOver={(e) => {
        if (!canDrop) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!isOver) setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);
        const id = e.dataTransfer.getData("text/influencerId");
        if (id && canDrop) onDrop(stage.stage, id);
      }}
      className={cn(
        "tw-flex tw-min-h-[320px] tw-w-[264px] tw-flex-shrink-0 tw-flex-col tw-gap-2 tw-rounded-xl tw-border tw-border-border tw-bg-muted/40 tw-p-2.5 tw-transition-colors",
        isOver && canDrop && "tw-border-blue-300 tw-bg-blue-50",
        draggingId !== null && !canDrop && "tw-opacity-40",
      )}
    >
      <div className="tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span
            className={cn(
              "tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold",
              toneStyle.pill,
            )}
          >
            <span className={cn("tw-h-1.5 tw-w-1.5 tw-rounded-full", toneStyle.dot)} />
            {label}
          </span>
          <span className="tw-rounded-full tw-border tw-border-border tw-bg-background tw-px-1.5 tw-text-[11px] tw-font-semibold tw-tabular-nums tw-text-muted-foreground">
            {creators.length}
          </span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-0.5">
          <button
            type="button"
            onClick={() =>
              downloadColumnCsv(
                label,
                creators,
                tagNameById,
                pipelineDefinition,
              )
            }
            disabled={creators.length === 0 || !tagCatalogReady}
            aria-label={`Download ${label} as CSV`}
            title={
              tagCatalogReady
                ? `Download ${label} as CSV`
                : "Waiting for tags to load…"
            }
            className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded tw-text-muted-foreground hover:tw-bg-accent hover:tw-text-foreground disabled:tw-pointer-events-none disabled:tw-opacity-40"
          >
            <Download className="tw-h-3.5 tw-w-3.5" />
          </button>
          {canAdd && (
            <button
              type="button"
              onClick={() => onAdd(stage.stage)}
              aria-label={`Add to ${label}`}
              className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded tw-text-muted-foreground hover:tw-bg-accent hover:tw-text-foreground"
            >
              <Plus className="tw-h-3.5 tw-w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="tw-h-0.5 tw-overflow-hidden tw-rounded-full tw-bg-muted">
        <div
          className="tw-h-full tw-rounded-full tw-opacity-50"
          style={{
            width: creators.length > 0 ? "100%" : 0,
            background: toneStyle.fill,
          }}
        />
      </div>

      <div className="tw-flex tw-flex-col tw-gap-2.5 tw-overflow-y-auto">
        {creators.length === 0 ? (
          <div className="tw-flex tw-h-14 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-dashed tw-border-border tw-text-[11px] tw-text-muted-foreground">
            Drop here or + add
          </div>
        ) : (
          creators.map((c) => (
            <CreatorCard
              key={c.influencerId}
              campaignId={campaignId}
              pipelineDefinition={pipelineDefinition}
              creator={c}
              isDragging={draggingId === c.influencerId}
              onDragStart={() => onDragStart(c)}
              onDragEnd={onDragEnd}
              onClick={() => onCardClick(c)}
            />
          ))
        )}
      </div>
    </div>
  );
}
